import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'src/store';
import { getNewProjectUserFormState } from 'src/app/admin/views/user-management/state/userManagementSelector';
import { NewProjectUserFormState, setFormState } from 'src/app/admin/views/user-management/state/userManagementSlice';
import { useCheckUsernameAvailableQuery } from 'src/app/admin/views/user-management/state/api/userManagementGraphSlice';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { isEmailIdValid } from 'src/shared/helpers/emailHelpers';
import useAuthService from 'src/shared/hooks/useAuthService';
import { formatError } from 'src/shared/utils/errorHelpers';
import { isCountryCodeValid as checkIsCountryCodeValid } from 'src/shared/helpers/phoneNumberHelpers';
import Input from 'src/shared/components/input/Input';
import ErrorMessage from 'src/shared/components/error-msg/ErrorMessage';

import styles from './CreateNewUserForm.module.scss';

/** New-user fields + email availability check; writes into `newProjectUserForm.formState` */
const CreateNewUserForm = () => {
    const formState = useAppSelector(getNewProjectUserFormState);
    const [isCountryCodeValid, setIsCountryCodeValid] = useState<boolean>(true);
    const [usernameAvailabilityError, setUsernameAvailabilityError] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const { currentCustomerId } = useCustomer();
    const { isCurrentUserSuperAdmin } = useAuthService();
    const skipUsernameCheck = !isEmailIdValid(formState.email) || !currentCustomerId;

    const { isFetching, isError, isSuccess, error, currentData } = useCheckUsernameAvailableQuery(
        {
            email: formState.email,
            customerId: currentCustomerId ?? '',
        },
        {
            skip: skipUsernameCheck,
        }
    );

    const onFormChange = (fragment: Partial<NewProjectUserFormState['formState']>) => {
        dispatch(
            setFormState({
                ...fragment,
            })
        );
    };

    useEffect(() => {
        if (skipUsernameCheck || isFetching) {
            return;
        }
        if (isError) {
            setUsernameAvailabilityError(formatError(error));
            dispatch(
                setFormState({
                    emailIdAvailabilityChecked: false,
                })
            );
            return;
        }
        if (isSuccess && currentData) {
            setUsernameAvailabilityError(null);
            dispatch(
                setFormState({
                    emailIdAvailable: currentData.usernameAvailable ?? false,
                    emailIdAvailabilityChecked: true,
                })
            );
        }
    }, [skipUsernameCheck, isFetching, isError, isSuccess, error, currentData, dispatch]);

    const onEmailChange = async (email: string) => {
        const isEmailValid = isEmailIdValid(email);

        setUsernameAvailabilityError(null);
        dispatch(
            setFormState({
                email,
                emailIdAvailabilityChecked: false,
                isEmailValid: isEmailValid,
            })
        );
    };

    const handleBlur = () => {
        setIsCountryCodeValid(!formState?.phoneCountryCode || checkIsCountryCodeValid(formState?.phoneCountryCode));
    };

    const emailHasError = Boolean(formState.email) && (!formState.emailIdAvailable || !isEmailIdValid(formState.email));
    const shouldShowEmailExistsError =
        isEmailIdValid(formState.email) && !formState.emailIdAvailable && formState.emailIdAvailabilityChecked;

    return (
        <div className={styles.createNewUserForm} data-testid='user-management-create-new-user-form'>
            {!isCurrentUserSuperAdmin && (
                <>
                    <div className={styles.stepIndicator}>Step 1 of 2</div>
                    <div className={styles.sectionHeading}>Enter details of new user</div>
                </>
            )}
            {isCurrentUserSuperAdmin && <div className={styles.title}>Create new user</div>}
            <div className={styles.fields}>
                <Input
                    value={formState.firstName}
                    placeholder='First name'
                    autoComplete='off'
                    onChange={event => onFormChange({ firstName: event.target.value })}
                    data-testid='user-management-new-user-first-name'
                />
                <Input
                    value={formState.lastName}
                    placeholder='Last name'
                    autoComplete='off'
                    onChange={event => onFormChange({ lastName: event.target.value })}
                    data-testid='user-management-new-user-last-name'
                />
                <div className={styles.phoneInput}>
                    <Input
                        value={formState.phoneCountryCode ?? ''}
                        placeholder='+44'
                        maxLength={4}
                        hideMaxLengthLabel={true}
                        onBlur={handleBlur}
                        hasError={!isCountryCodeValid}
                        onChange={event => onFormChange({ phoneCountryCode: event.target.value })}
                        data-testid='user-management-new-user-phone-country-code'
                    />
                    <Input
                        value={formState.phone ?? ''}
                        placeholder='Mobile number'
                        onChange={event => onFormChange({ phone: event.target.value })}
                        autoComplete='off'
                        maxLength={15}
                        hideMaxLengthLabel={true}
                        hasError={Boolean(formState.phone) && !isFinite(Number(formState.phone))}
                        onBlur={handleBlur}
                        data-testid='user-management-new-user-phone'
                    />
                </div>
                <Input
                    value={formState.email}
                    placeholder='Email'
                    hasError={emailHasError}
                    onChange={event => onEmailChange(event.target.value)}
                    data-testid='user-management-new-user-email'
                />

                {usernameAvailabilityError && <ErrorMessage message={<span>{usernameAvailabilityError}</span>} />}

                {shouldShowEmailExistsError && (
                    <ErrorMessage
                        message={
                            <>
                                This email address is already in use by a registered user
                                <p>Please enter a unique email address to register a new user</p>
                            </>
                        }
                    />
                )}

                {!isCountryCodeValid && (
                    <ErrorMessage message={<span>Please enter a valid country code starting with a &apos;+&apos;.</span>} />
                )}
            </div>
        </div>
    );
};

export default CreateNewUserForm;
